import { selectedClubId, selectClub, confirmClub, clubPath, ClubContext, ClubCapabilities } from '../state/club-session';
import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { from, Observable } from 'rxjs';
import {
  UserCompetitionsState,
  CompetitionStructure,
  BackendSquadResponse,
  GroupStandingItem,
  CompetitionMatchdayResponse,
  ActiveCompetitionContext,
  MatchLiveResponse,
  NextMatchResponse,
  TacticalLineupResponse,
  ConfirmSubstitutionWindowPayload,
  SubstitutionWindowResponse,
  SubstitutionWindow,
} from '../models/types';
import { apiSimulator } from './api-simulator.service';
import { competitionApi, extractActiveCompetitionContext } from './competition.api';

@Injectable({
  providedIn: 'root',
})
export class CompetitionService {
  private readonly http = inject(HttpClient);
  private readonly cache = new Map<string, { expiresAt: number; promise: Promise<any> }>();
  private contextGeneration = 0;

  private cachedRequest<T>(key: string, request: () => Promise<T>, forceRefresh = false): Promise<T> {
    const existing = this.cache.get(key);
    if (!forceRefresh && existing && existing.expiresAt > Date.now()) return existing.promise;
    const entry = { expiresAt: Infinity, promise: Promise.resolve().then(request) };
    this.cache.set(key, entry);
    entry.promise.then(() => {
      const midnight = new Date(Date.now());
      midnight.setHours(24, 0, 0, 0);
      entry.expiresAt = midnight.getTime();
    }, () => {
      if (this.cache.get(key) === entry) this.cache.delete(key);
    });
    return entry.promise;
  }

  private resolvedState: UserCompetitionsState | null = null;
  private cachedContext: ActiveCompetitionContext | null = null;
  readonly activeContext = signal<ActiveCompetitionContext>({ status: 'UNRESOLVED' });

  /**
   * Limpia la caché en memoria del contexto de competición del usuario.
   */
  clearContext(): void {
    this.contextGeneration++;
    this.cache.clear();
    this.resolvedState = null;
    this.cachedContext = null;
    this.activeContext.set({ status: 'UNRESOLVED' });
  }

  /**
   * Resuelve el contexto canónico de competición (seasonId, clubId, competitionUnitId)
   * consultando la fuente de verdad: GET /api/me/competitions.
   */
  async resolveCurrentCompetitionContext(
    forceRefresh: boolean = false
  ): Promise<ActiveCompetitionContext> {
    if (!forceRefresh && this.cachedContext?.status === 'JOINED' && this.cachedContext.clubId === selectedClubId()) return this.cachedContext;
    const generation = this.contextGeneration;

    try {
      const state = await this.getMyCompetitions(forceRefresh);
      if (generation !== this.contextGeneration) throw new Error('La sesión ha cambiado.');
      this.resolvedState = state;
      const global = extractActiveCompetitionContext(state);
      const club = selectedClubId() || global.clubId;
      if (!club) { this.activeContext.set(global); return global; }
      if (!selectedClubId()) selectClub(club);
      const [identity, gates] = await Promise.all([
        apiSimulator.callWithMockFallback<ClubContext>({method:'GET',endpoint:clubPath('/competition-context',club),service:'competition',mockName:'context',allowMockFallback:false}),
        apiSimulator.callWithMockFallback<ClubCapabilities>({method:'GET',endpoint:clubPath('/capabilities',club),service:'competition',mockName:'capabilities',allowMockFallback:false}),
      ]);
      if (generation !== this.contextGeneration || selectedClubId() !== club) throw new Error('El club seleccionado ha cambiado.');
      const member = state.privateLeagues?.find(p => p.club.id === club && p.privateLeagueId === identity.privateLeagueId);
      if (identity.mode === 'PRIVATE' && !member) throw new Error('No se pudo verificar la pertenencia al club.');
      confirmClub(identity,gates);
      const ctx: ActiveCompetitionContext = { ...(identity.mode === 'GLOBAL' ? global : {}),
        status:'JOINED', mode:identity.mode, clubId:club, seasonId:identity.seasonId || undefined,
        privateLeagueId:identity.privateLeagueId || undefined, competitionUnitId:identity.competitionUnitId || undefined,
        clubName:member?.club.name || global.clubName, competitionId:identity.privateLeagueId || global.competitionId,
        divisionName:identity.mode === 'PRIVATE' ? '' : global.divisionName };
      this.cachedContext = ctx;
      this.activeContext.set(ctx);
      return ctx;
    } catch (err: any) {
      if (generation !== this.contextGeneration) throw err;
      const errorCtx: ActiveCompetitionContext = {
        status: 'ERROR',
        errorMessage: err?.message || 'Error al resolver el contexto de competición del usuario.',
      };
      this.cachedContext = errorCtx;
      this.activeContext.set(errorCtx);
      throw err;
    }
  }

  /** Reuse the exact response that supplied the current competition context. */
  async getResolvedCompetitionState(): Promise<UserCompetitionsState> {
    await this.resolveCurrentCompetitionContext();
    return this.resolvedState!;
  }

  /**
   * Resuelve los identificadores activos de seasonId y clubId
   */
  async resolveActiveSeasonAndClubId(
    overrideSeasonId?: string | null,
    overrideClubId?: string | null
  ): Promise<{ seasonId: string; clubId: string }> {
    if (overrideSeasonId && overrideClubId) {
      return { seasonId: overrideSeasonId, clubId: overrideClubId };
    }
    const ctx = await this.resolveCurrentCompetitionContext();
    const seasonId = overrideSeasonId || ctx.seasonId;
    const clubId = overrideClubId || ctx.clubId;
    if (!seasonId || !clubId) {
      throw new Error('No se pudo resolver la temporada o el club activo del usuario autenticado.');
    }
    return { seasonId, clubId };
  }

  /**
   * Obtiene la jornada actual por defecto (o próxima entre jornadas) para el club activo
   * llamando a GET /api/competitions/seasons/{seasonId}/clubs/{clubId}/calendar
   */
  async getCurrentClubCalendar(
    overrideSeasonId?: string | null,
    overrideClubId?: string | null
  ): Promise<CompetitionMatchdayResponse> {
    const { seasonId, clubId } = await this.resolveActiveSeasonAndClubId(overrideSeasonId, overrideClubId);
    return this.getClubCalendar(seasonId, clubId);
  }

  /**
   * GET /api/competitions/seasons/{seasonId}/clubs/{clubId}/calendar
   * Calendario oficial del club y temporada obtenido del microservicio de competición.
   * Fallback mock: /mocks/competition/calendar.json
   */
  getClubCalendar$(
    seasonId: string,
    clubId: string,
    matchdayNumber?: number
  ): Observable<CompetitionMatchdayResponse> {
    return from(this.getClubCalendar(seasonId, clubId, matchdayNumber));
  }

  async getClubCalendar(
    seasonId: string,
    clubId: string,
    matchdayNumber?: number,
    forceRefresh = false
  ): Promise<CompetitionMatchdayResponse> {
    return this.cachedRequest(`calendar:${seasonId}:${clubId}:${matchdayNumber ?? 'current'}`, () => competitionApi.getClubCalendar(seasonId, clubId, matchdayNumber), forceRefresh);
  }

  /**
   * GET /api/me/competitions
   * Consulta el estado de competiciones del usuario
   * Fallback mock: /mocks/competition/me-competitions.json
   */
  getMyCompetitions$(): Observable<UserCompetitionsState> {
    return from(this.getMyCompetitions());
  }

  async getMyCompetitions(forceRefresh = false): Promise<UserCompetitionsState> {
    return this.cachedRequest('context', () => apiSimulator.callWithMockFallback<UserCompetitionsState>({
      method: 'GET',
      endpoint: '/api/me/competitions',
      service: 'competition',
      mockName: 'me-competitions',
      allowMockFallback: false,
    }), forceRefresh);
  }

  /** Identity for all common market and coach APIs; no client-side modality routing. */
  async getSelectedClubId(): Promise<string> {
    const context = await this.resolveCurrentCompetitionContext();
    if (!context.clubId) throw new Error('No se encontró el club seleccionado.');
    return context.clubId;
  }

  /**
   * GET /api/groups/{competitionGroupId}/standings
   * Clasificación en vivo del grupo de competición.
   * Fallback mock: /mocks/competition/standings.json
   */
  getGroupStandings$(
    competitionGroupId: string
  ): Observable<GroupStandingItem[]> {
    return from(this.getGroupStandings(competitionGroupId));
  }

  async getGroupStandings(
    competitionGroupId: string,
    forceRefresh = false
  ): Promise<GroupStandingItem[]> {
    return this.cachedRequest(`standings:${competitionGroupId}`, () => competitionApi.getGroupStandings(competitionGroupId), forceRefresh);
  }

  /**
   * GET /api/competitions/{competitionId}/structure
   * Estructura de la temporada activa
   * Fallback mock: /mocks/competition/competition-structure.json
   */
  getCompetitionStructure$(
    competitionId: string
  ): Observable<CompetitionStructure> {
    return from(this.getCompetitionStructure(competitionId));
  }

  async getCompetitionStructure(
    competitionId: string
  ): Promise<CompetitionStructure> {
    return apiSimulator.callWithMockFallback<CompetitionStructure>({
      method: 'GET',
      endpoint: `/api/competitions/${encodeURIComponent(competitionId)}/structure`,
      service: 'competition',
      mockName: 'competition-structure',
    });
  }

  /**
   * POST /api/clubs
   * Alta en competición
   * Fallback mock: /mocks/clubs/create-club.json
   */
  async createClub(name: string): Promise<any> {
    return apiSimulator.callWithMockFallback<any>({
      method: 'POST',
      endpoint: '/api/clubs',
      body: { name },
      service: 'clubs',
      mockName: 'create-club',
      transformMock: (mock) => ({
        ...mock,
        name,
        club: {
          ...(mock.club || {}),
          name,
        },
      }),
    });
  }

  /**
   * GET /api/clubs/{clubId}/squad
   * Recupera la plantilla de futbolistas del club
   * Fallback mock: /mocks/clubs/club-squad.json
   */
  getClubSquad$(clubId: string): Observable<BackendSquadResponse> {
    return from(this.getClubSquad(clubId));
  }

  async getClubSquad(clubId: string): Promise<BackendSquadResponse> {
    return apiSimulator.callWithMockFallback<BackendSquadResponse>({
      method: 'GET',
      endpoint: `/api/clubs/${encodeURIComponent(clubId)}/squad`,
      service: 'clubs',
      mockName: 'club-squad',
    });
  }

  /**
   * GET /api/matches/:fixtureId/live
   * Visualiza los jugadores y puntuaciones del partido.
   * Fallback mock: /mocks/competition/match-live.json
   */
  getMatchLive$(fixtureId: string): Observable<MatchLiveResponse> {
    return from(this.getMatchLive(fixtureId));
  }

  async getMatchLive(fixtureId: string): Promise<MatchLiveResponse> {
    return competitionApi.getMatchLive(fixtureId);
  }

  /**
   * GET /api/me/competitions/next-match
   * Datos específicos del próximo partido para el dashboard (homeClub, awayClub, stadium, sponsors).
   */
  getNextMatch$(): Observable<NextMatchResponse> {
    return from(this.getNextMatch());
  }

  async getNextMatch(forceRefresh = false): Promise<NextMatchResponse> {
    return this.cachedRequest(`next-match:${selectedClubId()}`, () => competitionApi.getNextMatch(), forceRefresh);
  }

  async getTacticalLineup(clubId: string, matchdayId: string): Promise<TacticalLineupResponse> {
    return competitionApi.getTacticalLineup(clubId, matchdayId);
  }

  async confirmSubstitutionWindow(
    clubId: string,
    matchdayId: string,
    payload: ConfirmSubstitutionWindowPayload,
    idempotencyKey: string
  ): Promise<SubstitutionWindowResponse> {
    return competitionApi.confirmSubstitutionWindow(clubId, matchdayId, payload, idempotencyKey);
  }

  async getSubstitutionWindows(clubId: string, matchdayId: string): Promise<SubstitutionWindow[]> {
    return competitionApi.getSubstitutionWindows(clubId, matchdayId);
  }
}
